import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { formatPhone, shortTime } from "../lib/format";
import { notificationText, notificationTitle } from "../lib/notifications";
import { useMarkNotificationsRead, useNotifications } from "../lib/queries";
import { BellIcon } from "./icons";
import { Avatar, Empty } from "./ui";

/** The bell in the menu: unread count, and the last 30 days of notifications. */
export function NotificationBell({ onPick }: { onPick?: () => void }) {
  const list = useNotifications();
  const unread = list.data?.unread ?? 0;
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="theme-toggle notify-bell"
        aria-label={unread ? `Notificaciones: ${unread} sin leer` : "Notificaciones"}
        title="Notificaciones"
        onClick={() => setOpen(true)}
      >
        <BellIcon />
        {unread > 0 && <span className="notify-bell-count">{unread > 99 ? "99+" : unread}</span>}
      </button>
      {open && (
        <NotificationsPanel
          onClose={() => setOpen(false)}
          onPick={() => {
            setOpen(false);
            onPick?.();
          }}
        />
      )}
    </>
  );
}

function NotificationsPanel({ onClose, onPick }: { onClose: () => void; onPick: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const list = useNotifications();
  const read = useMarkNotificationsRead();
  const navigate = useNavigate();
  const items = list.data?.items ?? [];
  useEffect(() => {
    dialog.current?.showModal();
  }, []);
  return (
    <dialog ref={dialog} className="notify-panel" aria-labelledby="notify-panel-title" onClose={onClose}>
      <div className="notify-panel-body">
        <div className="notify-panel-head">
          <h3 id="notify-panel-title">Notificaciones</h3>
          <span className="notify-panel-actions">
            {(list.data?.unread ?? 0) > 0 && (
              <button className="btn btn-sm" type="button" onClick={() => read.mutate({ all: true })}>
                Marcar todo como leído
              </button>
            )}
            <button
              className="notify-close"
              type="button"
              aria-label="Cerrar notificaciones"
              onClick={() => dialog.current?.close()}
            >
              ×
            </button>
          </span>
        </div>
        {items.length === 0 ? (
          <Empty title="Sin notificaciones.">
            <span>Aquí quedan los avisos de los últimos 30 días.</span>
          </Empty>
        ) : (
          <ul className="notify-list">
            {items.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  className={`notify-item${n.readAt ? "" : " unread"}`}
                  onClick={() => {
                    if (!n.readAt) read.mutate({ ids: [n.id] });
                    navigate({ to: "/bandeja", search: { c: n.conversationId, f: "all" } });
                    dialog.current?.close();
                    onPick();
                  }}
                >
                  <Avatar name={n.contactName} fallback={n.contactWaId} />
                  <span className="notify-text">
                    <span className="notify-title">{notificationTitle(n)}</span>
                    <span className="notify-who">{n.contactName ?? formatPhone(n.contactWaId)}</span>
                    {notificationText(n) && <span className="notify-body">{notificationText(n)}</span>}
                  </span>
                  <span className="faint notify-time">{shortTime(n.updatedAt)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        <p className="faint notify-panel-foot">Se guardan los últimos 30 días.</p>
      </div>
    </dialog>
  );
}
